import type {
  ColumnDef,
  ColumnSizingState,
  ExpandedState,
  OnChangeFn,
  Row,
  RowModel,
  Table,
  VisibilityState
} from '@tanstack/react-table';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback } from 'react';

import type { DataWithRowId } from '../types';

// IMPORTANT: Call these ONCE outside the hook to avoid expensive recalculations on every render
const coreRowModel = getCoreRowModel();
const expandedRowModel = getExpandedRowModel();

interface UseTableInstanceProps<T extends object, K extends object = T> {
  columnOrder: string[];
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  columns: ColumnDef<DataWithRowId<T, K>, unknown>[];
  data: DataWithRowId<T, K>[];
  expanded: ExpandedState;
  hasCustomExpandedRow: boolean;
  onColumnSizingChange: OnChangeFn<ColumnSizingState>;
  onExpandedChange: OnChangeFn<ExpandedState>;
}

interface UseTableInstanceReturn<T extends object, K extends object = T> {
  getHeaderGroups: Table<DataWithRowId<T, K>>['getHeaderGroups'];
  getRowModel: () => RowModel<DataWithRowId<T, K>>;
  rows: Row<DataWithRowId<T, K>>[];
}

export const useTableInstance = <T extends object, K extends object = T>(
  params: UseTableInstanceProps<T, K>
): UseTableInstanceReturn<T, K> => {
  const {
    data,
    columns,
    expanded,
    columnSizing,
    columnVisibility,
    columnOrder,
    hasCustomExpandedRow,
    onExpandedChange,
    onColumnSizingChange
  } = params;

  // Only use TanStack's nested row model when NOT using custom renderExpandedRow
  // When renderExpandedRow is provided, we render the custom content ourselves
  const getSubRows = useCallback(
    (row: DataWithRowId<T, K>) => {
      if (hasCustomExpandedRow) {
        return undefined; // Don't use TanStack's nested rows when custom expanded content is provided
      }

      return row.nestedRowItems as unknown as DataWithRowId<T, K>[] | undefined;
    },
    [hasCustomExpandedRow]
  );

  const getRowId = useCallback((row: DataWithRowId<T, K>) => row.uniqueTableRowIdentifier, []);

  const table = useReactTable<DataWithRowId<T, K>>({
    data,
    columns,
    state: {
      expanded,
      columnSizing,
      columnVisibility,
      columnOrder
    },
    onExpandedChange,
    onColumnSizingChange,
    getCoreRowModel: coreRowModel,
    getExpandedRowModel: expandedRowModel,
    getSubRows,
    getRowId,
    manualSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange'
  });

  return {
    getHeaderGroups: table.getHeaderGroups.bind(table),
    getRowModel: table.getRowModel.bind(table),
    rows: table.getRowModel().rows
  };
};
